import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminGet } from './adminApi';
import { DeviceQrCard } from './qr/DeviceQrCard';
import { ReallocateDevice } from './qr/ReallocateDevice';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const mono = { fontFamily: "'IBM Plex Mono', monospace" };
const STAGES = ['Received', 'SIM + ID', 'QC passed', 'In stock', 'Allocated', 'Active', 'Returned'];

const EVENT_LABEL = {
  RECEIVED: (p) => `Received from manufacturer · batch ${p.manufacturer_batch || '—'}`,
  SIM_BOUND: (p) => `SIM bound, ID issued (${p.trackotic_id || ''}) · ${p.operator || ''}`,
  QC_PASSED: (p) => `QC passed${p.tester ? ` · tester ${p.tester}` : ''}`,
  QC_FAILED: (p) => `QC failed · ${p.reason || ''}`,
  FIRMWARE_SET: (p) => `Firmware set ${p.to || ''}`,
  MOVED_TO_STOCK: () => 'Moved to stock',
  LABEL_PRINTED: () => 'Device QR label printed',
  LABEL_REPRINTED: (p) => `Label reprinted · ${p.reason || ''}`,
  RMA_SENT: (p) => `Sent to RMA · ${p.reason || ''}`,
};

function stageIndex(profile) {
  const s = profile.lifecycle_status;
  if (s === 'RETURNED') return 6;
  if (s === 'ACTIVE') return 5;
  if (s === 'PROVISIONED' || s === 'DELIVERED') return 4;
  if (s === 'IN_STOCK') return 3;
  const st = profile.assembly?.steps || {};
  if (st.QC_PASSED) return 2;
  if (st.SIM_BOUND) return 1;
  return 0;
}

export default function AdminDeviceProfilePage() {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);

  useEffect(() => {
    adminGet(`/devices/${deviceId}/profile`).then(setP).catch(e => toast.error(e.message));
  }, [deviceId]);

  useEffect(() => {
    if (q.length < 3) { setResults([]); return; }
    const t = setTimeout(() => adminGet(`/devices/search?q=${encodeURIComponent(q)}`).then(setResults).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [q]);

  if (!p) return <div className="p-8" data-testid="device-profile-loading">Loading…</div>;
  const active = stageIndex(p);

  return (
    <div className="p-8 max-w-7xl" data-testid="device-profile-page">
      <div className="relative mb-6 flex items-center gap-4">
        <span className="text-sm shrink-0" style={{ color: '#6b7069' }}>Find device</span>
        <div className="relative flex-1">
          <Input data-testid="device-search-input" value={q} onChange={e => setQ(e.target.value)}
            placeholder="Scan QR or type Track0tic ID, IMEI or MSISDN" className="bg-white border-[#D9D6CD]" />
          {results.length > 0 && (
            <div className="absolute z-20 top-full mt-1 w-full bg-white border rounded-md shadow-lg" style={{ borderColor: '#D9D6CD' }}>
              {results.map(r => (
                <button key={r.id} data-testid={`search-result-${r.id}`} className="w-full text-left px-4 py-2 hover:bg-[#FAF9F6] text-sm"
                  onClick={() => { setQ(''); setResults([]); navigate(`/admin/devices/${r.id}`); }}>
                  <span style={mono}>{r.trackotic_id || r.imei}</span>
                  <span className="ml-2 text-xs" style={{ color: '#9a9e96' }}>{r.device_type_display} · {r.lifecycle_status}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 mb-1">
        <h1 className="text-4xl font-bold" style={{ ...mono, color: '#1B1D1C' }} data-testid="device-profile-id">{p.trackotic_id || p.imei}</h1>
        <span className="px-2.5 py-1 rounded text-xs" style={{ background: '#E7F2EE', color: '#0B6B55' }}>
          {p.lifecycle_status}{p.assembly?.rma ? ' · RMA' : ''}
        </span>
      </div>
      <p className="text-sm mb-5" style={{ color: '#6b7069' }}>
        {p.device_type_display} · IMEI <span style={mono}>{p.imei}</span> · MSISDN <span style={mono}>{p.msisdn || '—'}</span> · Firmware <span style={mono}>{p.firmware || '—'}</span>
      </p>

      <div className="flex gap-1.5 mb-6 flex-wrap" data-testid="lifecycle-strip">
        {STAGES.map((s, i) => (
          <div key={s} className={cn('px-4 py-2 rounded text-sm', i === active ? 'text-white font-medium' : '')}
            style={i === active ? { background: '#0B6B55' } : i < active ? { background: '#DBD8CF', color: '#4b504a' } : { background: '#EFEDE6', color: '#9a9e96', border: i === STAGES.length - 1 ? '1px dashed #C9C6BC' : 'none' }}>
            {s}
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <div className="rounded-lg border bg-white p-5" style={{ borderColor: '#E5E3DC' }} data-testid="now-with-card">
          <p className="text-sm" style={{ color: '#6b7069' }}>Now with</p>
          <p className="text-2xl font-bold mt-1" style={{ color: '#1B1D1C' }}>{p.current_assignment?.customer || (p.lifecycle_status === 'IN_STOCK' ? 'Track0tic stock' : 'Assembly centre')}</p>
          <p className="text-sm mt-1" style={{ color: '#9a9e96' }}>
            {p.current_assignment ? `since ${new Date(p.current_assignment.from).toLocaleDateString('en-IN')}` : '—'}
          </p>
        </div>
        <div className="rounded-lg border bg-white p-5" style={{ borderColor: '#E5E3DC' }} data-testid="site-card">
          <p className="text-sm" style={{ color: '#6b7069' }}>Assigned site</p>
          <p className="text-2xl font-bold mt-1" style={{ color: '#1B1D1C' }}>{p.current_assignment?.site || '—'}</p>
        </div>
        <div className="rounded-lg border bg-white p-5" style={{ borderColor: '#E5E3DC' }} data-testid="position-card">
          <p className="text-sm" style={{ color: '#6b7069' }}>Last known position</p>
          <p className="text-2xl font-bold mt-1" style={{ ...mono, color: '#1B1D1C' }}>
            {p.last_position ? `${Number(p.last_position.lat).toFixed(4)}, ${Number(p.last_position.lng).toFixed(4)}` : '—'}
          </p>
          <p className="text-sm mt-1" style={{ color: '#9a9e96' }}>
            {p.last_position?.at ? `last seen ${new Date(p.last_position.at).toLocaleString('en-IN')}` : 'No telemetry yet'}
          </p>
        </div>
      </div>

      <div className="mb-6 max-w-lg"><DeviceQrCard deviceId={deviceId} /><ReallocateDevice deviceId={deviceId} onDone={() => window.location.reload()} /></div>

      <div className="grid lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 rounded-lg border bg-white p-5" style={{ borderColor: '#E5E3DC' }} data-testid="customer-history">
          <h2 className="text-lg font-bold mb-3" style={{ color: '#1B1D1C' }}>Customer history</h2>
          <table className="w-full text-sm">
            <thead><tr className="text-left border-b" style={{ color: '#6b7069', borderColor: '#EDEBE4' }}>
              <th className="py-2 font-medium">Customer</th><th className="font-medium">Site</th><th className="font-medium">From</th>
              <th className="font-medium">To</th><th className="font-medium">Trips</th><th className="font-medium">Km</th><th className="font-medium">Exit reason</th></tr></thead>
            <tbody>
              {p.customer_history.map((h) => (
                <tr key={`${h.customer}-${h.from}`} className="border-b" style={{ borderColor: '#F3F1EA' }}>
                  <td className={cn('py-2.5', !h.to && 'font-bold')}>{h.customer}</td>
                  <td>{h.site || '—'}</td>
                  <td>{new Date(h.from).toLocaleDateString('en-IN')}</td>
                  <td>{h.to ? new Date(h.to).toLocaleDateString('en-IN') : <span style={{ color: '#0B6B55' }}>Current</span>}</td>
                  <td style={mono}>{h.trips}</td><td style={mono}>{h.km}</td>
                  <td style={{ color: '#6b7069' }}>{h.exit_reason || '—'}</td>
                </tr>
              ))}
              {p.customer_history.length === 0 && <tr><td colSpan={7} className="py-6 text-center" style={{ color: '#9a9e96' }}>Not yet allocated to a customer</td></tr>}
            </tbody>
          </table>
          <p className="text-xs mt-3" style={{ color: '#9a9e96' }}>Each customer sees only their own period of this device's data.</p>
        </div>

        <div className="lg:col-span-2 rounded-lg border bg-white p-5" style={{ borderColor: '#E5E3DC' }} data-testid="custody-timeline">
          <h2 className="text-lg font-bold mb-3" style={{ color: '#1B1D1C' }}>Custody timeline</h2>
          <div className="space-y-0">
            {p.custody_timeline.map((e, i) => (
              <div key={`${e.date}-${i}`} className="flex gap-3 py-2.5 border-b text-sm" style={{ borderColor: '#F3F1EA' }}>
                <span className="shrink-0 w-24" style={{ ...mono, color: '#9a9e96' }}>{new Date(e.date).toLocaleDateString('en-IN')}</span>
                <span style={{ color: '#1B1D1C' }}>
                  {(EVENT_LABEL[e.event_type] || (() => e.event_type))(e.payload || {})}
                  {e.actor ? <span style={{ color: '#9a9e96' }}> · by {e.actor}</span> : null}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
